import { useCallback, useState } from 'react';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { log } from '../../loggers/Logger';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';

/** Read-only presentation state for the "download PDF" affordance. */
export interface DownloadPdfViewModel {
  /** Whether the button is shown (published content only). */
  readonly isVisible: boolean;
  /** True while the pre-signed URL is being fetched. */
  readonly isDownloading: boolean;
  /** Fetch the publication PDF's pre-signed URL and open it in a new tab. */
  readonly download: () => void;
}

/**
 * Drives the "PDF" button on the read-only view page. Visible only for
 * publications (`read-publication`); drafts and finalizing copies have no
 * downloadable PDF.
 */
export function useDownloadPdfViewModel(): DownloadPdfViewModel {
  const service = useContentEditorService();
  const mpl = useMplServiceClient();

  const handle = service.loadedDocumentHandle;
  const isVisible = handle?.options.kind === 'read-publication';

  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!handle || handle.options.kind !== 'read-publication') return;

    const { workflowId } = handle.options;

    // Open the tab synchronously to keep the click's user-gesture context;
    // popup blockers reject window.open once we've awaited the pre-signed URL.
    const pdfTab = window.open('', '_blank');

    setIsDownloading(true);
    try {
      const { preSignedUrl } = await mpl.getPublicationPDF(handle.contentId, workflowId);
      if (pdfTab) pdfTab.location.href = preSignedUrl;
    } catch (err) {
      pdfTab?.close();
      log.error({ err, contentId: handle.contentId }, 'Failed to download publication PDF');
      // The PDF button has no inline alert slot; surface via the flashbar.
      const detail =
        err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.';
      FlashbarMessageBroker.create().pushErrorMessage(`Failed to download the PDF: ${detail}`);
    } finally {
      setIsDownloading(false);
    }
  }, [handle, mpl]);

  return {
    isVisible,
    isDownloading,
    download,
  };
}
